import { BankMapFormValues } from "components/ChartOfAccounts/ImportCOA/MapDataConnectionsStep";
import { FormValues as CategoryMapFormValues } from "components/ChartOfAccounts/ImportCOA/MapCategoriesStep";
import { STEPS, STEPS_TYPE } from "constants/qboImportSteps";
import { ModalClose } from "hooks/useModal";
import { createContext, ReactNode, useContext, useState } from "react";

export type FinallyAddDetailsFormValues = {
  date_till_import: string;
  prev_fy_end_date: string;
  coa_file: File | null;
  je_files: File[];
};

type ImportFinallyContextType = {
  entityId: string;
  currentStep: STEPS_TYPE;
  onNext: () => void;
  onStepChange: (step: STEPS_TYPE) => void;
  importProcessId: string | null;
  setImportProcessId: (id: string) => void;
  addDetailsFormValues: FinallyAddDetailsFormValues | null;
  setAddDetailsFormValues: (values: FinallyAddDetailsFormValues) => void;
  bankMapFormValues: BankMapFormValues | null;
  setBankMapFormValues: (values: BankMapFormValues) => void;
  categoryMapFormValues: CategoryMapFormValues | null;
  setCategoryMapFormValues: (values: CategoryMapFormValues) => void;
  isOpen: boolean;
  close: ModalClose;
};

type ImportFinallyProviderProps = {
  children: ReactNode;
  entityId: string;
  resumeImportProcessId: string | null;
  isOpen: boolean;
  close: ModalClose;
};

const STEP_ORDER = Object.values(STEPS);

const ImportFinallyContext = createContext<
  ImportFinallyContextType | undefined
>(undefined);

export const useImportFinally = (): ImportFinallyContextType => {
  const context = useContext(ImportFinallyContext);

  if (context === undefined) {
    throw new Error(
      "useImportFinally must be used within an ImportFinallyProvider"
    );
  }

  return context;
};

export const ImportFinallyProvider = ({
  children,
  entityId,
  resumeImportProcessId,
  isOpen,
  close,
}: ImportFinallyProviderProps) => {
  const [currentStep, setCurrentStep] = useState<STEPS_TYPE>(() =>
    resumeImportProcessId ? STEPS.MAP_DATA_CONNECTIONS : STEPS.ADD_DETAILS
  );
  const [importProcessId, setImportProcessId] = useState<string | null>(
    resumeImportProcessId
  );
  const [addDetailsFormValues, setAddDetailsFormValues] =
    useState<FinallyAddDetailsFormValues | null>(null);
  const [bankMapFormValues, setBankMapFormValues] =
    useState<BankMapFormValues | null>(null);
  const [categoryMapFormValues, setCategoryMapFormValues] =
    useState<CategoryMapFormValues | null>(null);

  const onNext = () => {
    if (currentStep === STEPS.REVIEW) {
      close();
      return;
    }

    setCurrentStep(STEP_ORDER[STEP_ORDER.indexOf(currentStep) + 1]);
  };

  const contextValue: ImportFinallyContextType = {
    entityId,
    currentStep,
    onNext,
    onStepChange: setCurrentStep,
    importProcessId,
    setImportProcessId,
    addDetailsFormValues,
    setAddDetailsFormValues,
    bankMapFormValues,
    setBankMapFormValues,
    categoryMapFormValues,
    setCategoryMapFormValues,
    isOpen,
    close,
  };

  return (
    <ImportFinallyContext.Provider value={contextValue}>
      {children}
    </ImportFinallyContext.Provider>
  );
};
